import { useEffect, useMemo, useState, useCallback } from "react";
import { BrowserProvider, JsonRpcSigner, Provider, Signer } from "ethers";
import {
  useAccount,
  useConnect,
  useDisconnect,
  useWalletClient,
} from "wagmi";
import { ADDRESSES, getContracts, getProvider } from "@/lib/contracts";

export function useContracts() {
  const { address, isConnected, chainId } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();

  const [signer, setSigner] = useState<Signer | null>(null);

  useEffect(() => {
    if (!walletClient || !address || !chainId) {
      setSigner(null);
      return;
    }

    // 把 viem walletClient 包装成符合 EIP-1193 的最小 provider
    const eip1193Provider: import("ethers").Eip1193Provider = {
      request: (args: { method: string; params?: unknown[] | Record<string, unknown> }) =>
        walletClient.request(args as Parameters<typeof walletClient.request>[0]),
    };

    const provider = new BrowserProvider(eip1193Provider, chainId);

    provider
      .getSigner(address)
      .then((s) => setSigner(s as JsonRpcSigner))
      .catch(() => setSigner(null));
  }, [walletClient, address, chainId]);

  const provider = useMemo<Provider>(() => {
    return signer ? (signer.provider as BrowserProvider) : getProvider();
  }, [signer]);

  const contracts = useMemo(() => getContracts(signer ?? provider), [signer, provider]);

  const requestConnect = useCallback(async () => {
    const injected =
      connectors.find(
        (c) =>
          c.id === "injected" ||
          c.name.toLowerCase().includes("metamask") ||
          c.name.toLowerCase().includes("rabby")
      ) ?? connectors[0];
    if (injected) connect({ connector: injected });
  }, [connect, connectors]);

  const isCorrectChain = chainId === ADDRESSES.chainId;

  return {
    ...contracts,
    signer,
    provider,
    account: address ?? null,
    chainId: chainId ?? null,
    connected: isConnected && isCorrectChain,
    isCorrectChain,
    requestConnect,
    disconnect: () => disconnect(),
    MINT_PRICE_WEI: BigInt(import.meta.env.VITE_MINT_PRICE_WEI ?? "10000000000000000"),
  };
}
